import { useState, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';

interface EditableTextProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  as?: 'h1' | 'h2' | 'h3' | 'p' | 'span';
  placeholder?: string;
}

export function EditableText({ 
  value, 
  onChange, 
  className,
  as: Component = 'span',
  placeholder = 'Click to edit...'
}: EditableTextProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const editableRef = useRef<HTMLElement>(null);

  useEffect(() => {
    setEditValue(value);
    // Also update the DOM content when value changes externally
    if (editableRef.current && !isEditing) {
      editableRef.current.textContent = value;
    }
  }, [value, isEditing]);

  const handleInput = (e: React.FormEvent<HTMLElement>) => {
    setEditValue(e.currentTarget.textContent || '');
  };

  const handleEditableKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      e.currentTarget.blur();
    }
    if (e.key === 'Escape') {
      if (editableRef.current) {
        editableRef.current.textContent = value;
      }
      setEditValue(value);
      setIsEditing(false);
    }
  };

  const handleEditableBlur = () => {
    setIsEditing(false);
    if (editValue !== value) {
      onChange(editValue);
    }
  };

  const handleClick = () => {
    if (!isEditing) {
      setIsEditing(true);
      // Focus and select after state update
      setTimeout(() => {
        if (editableRef.current) {
          editableRef.current.focus();
          // Select all text
          const range = document.createRange();
          range.selectNodeContents(editableRef.current);
          const selection = window.getSelection();
          selection?.removeAllRanges();
          selection?.addRange(range);
        }
      }, 0);
    }
  };

  return (
    <Component
      ref={editableRef as React.RefObject<HTMLHeadingElement & HTMLParagraphElement & HTMLSpanElement>}
      contentEditable={isEditing}
      suppressContentEditableWarning
      onInput={handleInput}
      onBlur={handleEditableBlur}
      onKeyDown={handleEditableKeyDown}
      onClick={handleClick}
      className={cn(
        'cursor-pointer rounded-sm transition-all outline-none',
        !isEditing && 'hover:ring-2 hover:ring-primary hover:ring-offset-2',
        isEditing && 'ring-2 ring-primary ring-offset-2 bg-primary/5',
        !value && !isEditing && 'text-muted-foreground italic',
        className
      )}
      style={{
        minHeight: isEditing ? '1em' : undefined,
      }}
    >
      {value || placeholder}
    </Component>
  );
}